/**
 * Failure episode tracking — decides when a service problem is real enough to
 * be worth a permanent audit record.
 *
 * Health probes flap. A service can go red for a few seconds because a Cloud Run
 * instance was cold, a connection was reset, or a poll happened to land during a
 * revision swap. Archiving each of those would bury the outages that matter in
 * noise, so a packet is written only when a failure is either:
 *
 *   DEFINITE  — the error code proves a structural fault that will not clear on
 *               its own (a deleted nginx route, a 404 health path, an
 *               unconfigured URL, a rejected signature). One observation is
 *               enough; waiting adds nothing.
 *   PERSISTENT— the failure survived several consecutive probes spanning at
 *               least the dwell window. This is the gate that transient blips
 *               fail to clear.
 *
 * Anything else is TRANSIENT and is remembered in the episode's observation
 * history — where it still informs root-cause analysis if the episode later
 * escalates — but never triggers a write on its own.
 */

export const PERSISTENCE_MIN_OBSERVATIONS = 3;
export const PERSISTENCE_MIN_DWELL_MS = 90_000;
/** While an episode is still open, refresh its record on this cadence. */
export const EPISODE_REREPORT_MS = 15 * 60_000;
export const MAX_OBSERVATIONS = 20;

/**
 * A short failure that cleared on its own is still worth one line in the log.
 *
 * Before this, a blip that lasted less than the dwell window was remembered in
 * memory and then thrown away on recovery. An operator could watch the button
 * turn red, go to the super-panel, and find nothing at all. So a transient
 * episode is now filed when it closes.
 *
 * The cooldown is what stops that flooding the store. A service that flaps every
 * thirty seconds writes one record an hour, not one record a flap, and the
 * record carries the count of how many times it happened.
 */
export const TRANSIENT_COOLDOWN_MS = 60 * 60_000;

/**
 * Error codes that are structural rather than transient. A timeout or a dropped
 * connection is deliberately absent: those are exactly the flaps the dwell
 * window exists to filter.
 */
const DEFINITE_ERROR_CODES = new Set([
    'URL_UNKNOWN',
    'MAIL_ROUTE_MISSING',
    'MAIL_HEALTH_WRONG_SERVICE',
    'MAIL_HEALTH_UNPARSEABLE',
    'MAIL_HEALTH_TOKEN_MISSING',
    'MAIL_HEALTH_TOKEN_REJECTED',
    'MAIL_SIGNATURE_REJECTED',
    'MAIL_SECRET_MISSING',
]);

/** True when the code proves a fault that will not resolve without intervention. */
export function isDefiniteFailure(errorCode) {
    if (!errorCode) return false;
    if (DEFINITE_ERROR_CODES.has(errorCode)) return true;
    // Any 4xx/5xx from a health path is a definite fault: the service answered,
    // and answered wrongly. (A blip shows up as TIMEOUT or UNREACHABLE instead.)
    return /^(MAIL_PROBE_)?HTTP_[45]\d\d$/.test(errorCode);
}

function isFailing(status) {
    return status === 'red' || status === 'yellow';
}

/**
 * Is there anything here worth a record?
 *
 * Two kinds of thing qualify. A failing reading is the obvious one. The other is
 * a check problem: the service answered, so no colour changes and no customer
 * sees anything, but the answer shows our own check is pointed at the wrong
 * place. A health path returning 404 for months is exactly that, and under the
 * old rules it left no trace anywhere.
 */
function isRecordable(result) {
    return isFailing(result.status) || Boolean(result.checkIssue);
}

/**
 * Track the last time a transient record was written per service, so a flapping
 * service cannot fill the store.
 *
 * Held beside the episode map rather than inside it, so `episodes` keeps meaning
 * exactly one thing: the failures that are open right now.
 */
const transientClocks = new WeakMap();

function transientClock(episodes) {
    if (!transientClocks.has(episodes)) transientClocks.set(episodes, new Map());
    return transientClocks.get(episodes);
}

/**
 * Fold one poll's results into the running episode state.
 *
 * @param {Map<string, object>} episodes — mutated in place; keyed by service key
 * @param {Array<object>} results — this poll's service results
 * @param {number} now — epoch ms, injected so tests need no clock control
 * @returns {Array<object>} episodes that should be written to the audit store now
 */
export function updateEpisodes(episodes, results, now = Date.now()) {
    const dueForReport = [];

    for (const result of results) {
        const key = result.key;
        const observation = {
            at: new Date(now).toISOString(),
            status: result.status,
            // What the operator was actually shown, after the confirmation rule
            // and the critical/secondary rule. Recording it is the only way to
            // answer "the button stayed green, was anything wrong?" afterwards.
            displayed_status: result.displayedStatus ?? result.status,
            latency_ms: result.latencyMs ?? null,
            summary: result.summary || '',
            error_code: result.errorCode || null,
            http_status: result.httpStatus ?? null,
            check_issue: Boolean(result.checkIssue),
        };

        if (!isRecordable(result)) {
            // Recovery closes the episode. The next failure starts a fresh one,
            // so a service that flaps in and out is not reported as one long
            // outage. A short episode that was never reported is filed on the
            // way out, subject to the cooldown, so it leaves a trace.
            const closing = episodes.get(key);
            episodes.delete(key);
            if (closing && closing.reported_at === null) {
                const clock = transientClock(episodes);
                const lastFiled = clock.get(key) || 0;
                if (now - lastFiled >= TRANSIENT_COOLDOWN_MS) {
                    clock.set(key, now);
                    closing.classification = 'transient';
                    closing.ended_at = new Date(now).toISOString();
                    closing.dwell_ms = now - Date.parse(closing.started_at);
                    closing.reported_at = new Date(now).toISOString();
                    dueForReport.push(closing);
                }
            }
            continue;
        }

        let episode = episodes.get(key);
        if (!episode) {
            episode = {
                key: `${key}:${new Date(now).toISOString()}`,
                service_key: key,
                service_label: result.label || key,
                started_at: new Date(now).toISOString(),
                observations: [],
                reported_at: null,
                classification: null,
                // False until a poll actually puts a colour in front of someone.
                // A record where this stays false is a problem we caught without
                // anybody phoning in about it.
                shown_to_operator: false,
            };
            episodes.set(key, episode);
        }
        if (isFailing(observation.displayed_status)) episode.shown_to_operator = true;

        episode.observations.push(observation);
        if (episode.observations.length > MAX_OBSERVATIONS) {
            // Keep the first observation — the onset is the most diagnostic
            // moment — and drop from the middle as the episode runs long.
            episode.observations.splice(1, 1);
        }
        episode.last_seen_at = observation.at;
        episode.worst_status = episode.worst_status === 'red' || result.status === 'red' ? 'red' : result.status;
        episode.probed_url = result.probedUrl || episode.probed_url || null;
        episode.error_codes = Array.from(new Set(
            episode.observations.map((o) => o.error_code).filter(Boolean)
        ));

        const definite = isDefiniteFailure(result.errorCode);
        const dwellMs = now - Date.parse(episode.started_at);
        const persistent = episode.observations.length >= PERSISTENCE_MIN_OBSERVATIONS
            && dwellMs >= PERSISTENCE_MIN_DWELL_MS;

        if (!definite && !persistent) {
            episode.classification = 'transient';
            continue;
        }

        episode.classification = definite ? 'definite' : 'persistent';
        episode.dwell_ms = dwellMs;

        const neverReported = episode.reported_at === null;
        const stale = !neverReported && now - Date.parse(episode.reported_at) >= EPISODE_REREPORT_MS;
        if (neverReported || stale) {
            episode.reported_at = new Date(now).toISOString();
            dueForReport.push(episode);
        }
    }

    return dueForReport;
}
